import { useReportUrlFilters } from '@src/packages/dashboard/hooks/useReportUrlFilters';
import { useSyncReportUrl } from '@src/packages/dashboard/hooks/useSyncReportUrl';
import { selectCalendarTimeZone, getCalendarRange } from '@src/packages/utils/dates/calendar';
import { DateUtil } from '@src/packages/utils/dates';
/* eslint-disable react-hooks/exhaustive-deps */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router-dom';

import { restoreClearedReportFilters } from '@src/packages/dashboard/utils/restoreClearedReportFilters';

import type { SetValueProps } from '@src/components/FilterDropDown/FilterDropDown.interfaces';
import { useSecondaryPanel } from '@src/components/SecondaryPanel/SecondaryPanel';
import { SCHEDULE_TYPES } from '@src/constants/commons';
import { auth } from '@src/firebase/firebase';
import { getRouteToCheckpointRoutesDrawer, PATHS } from '@src/packages/dashboard/constants';
import { camelize, getRange } from '@src/packages/dashboard/utils';
import { getFullUrl, useElasticDivisionIds } from '@src/packages/utils';
import { disableCalendarDatesAfterToday } from '@src/packages/utils/dates';
import useGetParams from '@src/packages/utils/hooks/useGetParams';
import { get as getLocal, remove as removeLocal } from '@src/packages/utils/storage';
import { get, set } from '@src/packages/utils/sessionStorage';
import { useDivisionNames } from '@src/hooks/useDivisionNames';
import type { CommonReduxState } from '@src/models/commons/reduxState.interface';
import { apiCallable } from '@src/util/cloud-functions/apiClient';
import { API_OPERATIONS } from '@src/util/cloud-functions/operations';

import { calculateScheduleProgress } from '../../SchedulesDashboard/constants';
import {
  getDashboardScheduleDivisionIds,
  getDashboardSchedulesById,
  getDashboardSchedulesWithDivisionNames,
} from '../../utils/getDashboardSchedulesById';
import {
  areScheduleSelectionsEqual,
  buildCheckpointRoutesFilterSetValueProps,
  buildDefaultCheckpointRoutesChartFilters,
  buildInitCheckpointRoutesFilterValues,
  CHECKPOINT_ROUTES_DASHBOARD_FILTER_FIELDS,
  CHECKPOINT_ROUTES_DASHBOARD_FILTERS_STORAGE_KEY,
  checkpointRoutesFiltersToStoragePayload,
  filterValuesToCheckpointRoutesFilters,
  filterValuesToCheckpointRoutesFiltersUi,
  mergeFilterSetValueProps,
  normalizeStoredCheckpointRoutesFilters,
  SCHEDULES_REPORT_MAX_SPAN,
  type CheckpointRoutesChartFilters,
} from '../checkpointRoutesFilterConfig';
import type {
  CheckpointRoutesDashboardContextValue,
  CheckpointRoutesDashboardReduxState,
  GoToTimelineParams,
  ScheduleDashboardDrawerState,
  ScheduleDashboardDrawerType,
  SelectedSchedulesState,
  SelectedUsersState,
  TableDataByDay,
  TimeAggregationBucket,
} from '../types';

const EMPTY_ASSIGNED_DIVISIONS: CommonReduxState['divisions']['assigned']['data']['divisions'] = {};

export const CheckpointRoutesDashboardContext =
  createContext<CheckpointRoutesDashboardContextValue | null>(null);

interface CheckpointRoutesDashboardProviderProps {
  children: ReactNode;
}

export const CheckpointRoutesDashboardProvider = ({
  children,
}: CheckpointRoutesDashboardProviderProps) => {
  const intl = useIntl();
  const { values: filters, setValue: setFilters, toggle, isOpen } = useSecondaryPanel();

  const urlFilters = useReportUrlFilters('routes');
  const storedSnapshot = useMemo(() => {
    if (urlFilters) return normalizeStoredCheckpointRoutesFilters(urlFilters);
    const sessionValue = get(CHECKPOINT_ROUTES_DASHBOARD_FILTERS_STORAGE_KEY);
    if (sessionValue) return normalizeStoredCheckpointRoutesFilters(sessionValue);

    // Preserve the previous localStorage preference once, then keep it scoped to this session.
    const legacyLocalValue = getLocal(CHECKPOINT_ROUTES_DASHBOARD_FILTERS_STORAGE_KEY);
    if (legacyLocalValue) {
      set(CHECKPOINT_ROUTES_DASHBOARD_FILTERS_STORAGE_KEY, legacyLocalValue);
      removeLocal(CHECKPOINT_ROUTES_DASHBOARD_FILTERS_STORAGE_KEY);
    }
    return normalizeStoredCheckpointRoutesFilters(legacyLocalValue);
  }, []);

  const initialChartFilters = useMemo(
    () => storedSnapshot ?? buildDefaultCheckpointRoutesChartFilters(),
    [storedSnapshot],
  );

  const initialFiltersRef = useRef(
    buildInitCheckpointRoutesFilterValues(initialChartFilters, intl),
  );

  /**
   * The panel seeds `filters` with `initialFilters` in a mount effect, so the first render still
   * sees an empty map. Falling back to the persisted filters keeps the view aligned with what the
   * panel is about to show.
   */
  const draftChartFilters = useMemo(
    () => filterValuesToCheckpointRoutesFiltersUi(filters, initialChartFilters),
    [filters, initialChartFilters],
  );
  const [chartFilters, setChartFilters] = useState(initialChartFilters);
  const hasPendingFilters =
    JSON.stringify(checkpointRoutesFiltersToStoragePayload(draftChartFilters)) !==
    JSON.stringify(checkpointRoutesFiltersToStoragePayload(chartFilters));

  const { relative, dateRange, groupBy, schedules: selectedSchedules } = chartFilters;
  const { isRelative, relativeAmount, dateType } = relative;

  const [aggregationTimeZone, setAggregationTimeZone] = useState('UTC');
  const aggregationTimeZoneRef = useRef<string | null>(null);
  const [aggregations, setAggregations] = useState<TimeAggregationBucket[]>([]);
  const [routesById, setRoutesById] = useState<Record<string, SelectedSchedulesState['byId'][string]>>({});
  const [loading, setLoading] = useState(false);
  const [scheduleSelectorLoading, setScheduleSelectorLoading] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<SelectedUsersState | null>(null);
  const [drawerState, setDrawerState] = useState<ScheduleDashboardDrawerState>({
    users: false,
  });

  const selectedDivs = useSelector(
    ({ divisions }: CheckpointRoutesDashboardReduxState) => divisions.selector.ids || [],
  );
  const queryDivisionIds = useElasticDivisionIds();
  useSyncReportUrl(chartFilters, 'routes', selectedDivs);

  const orgId = useSelector(
    (state: CheckpointRoutesDashboardReduxState) => state.organizations.organization.id,
  );

  const organizationTimeZone = useSelector(selectCalendarTimeZone);
  const assignedDivisions = useSelector(
    (state: CommonReduxState) =>
      state.divisions.assigned.data?.divisions ?? EMPTY_ASSIGNED_DIVISIONS,
  );
  const q = useGetParams();
  const history = useHistory<{ shadow?: boolean }>();
  const { url } = useRouteMatch();
  const { location: { state: { shadow = false } = {} } = {} } = history || {};

  const divId = q.get('divId');
  const triggerId = q.get('triggerId')?.replace('/events', '');
  const dateKey = q.get('dateKey');
  const openTimelineEvents = !!divId && !!triggerId;

  const selectedSchedulesRef = useRef(draftChartFilters.schedules);

  const intlRef = useRef(intl);
  intlRef.current = intl;

  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const initialFetchKeyRef = useRef<string | null>(null);

  const fetchParamsRef = useRef({
    orgId,
    organizationTimeZone,
    selectedDivs,
    queryDivisionIds,
    relative,
    dateRange,
    dateType,
    isRelative,
    relativeAmount,
    groupBy,
    selectedSchedules,
  });

  fetchParamsRef.current = {
    orgId,
    organizationTimeZone,
    selectedDivs,
    queryDivisionIds,
    relative,
    dateRange,
    dateType,
    isRelative,
    relativeAmount,
    groupBy,
    selectedSchedules,
  };
  const chartFiltersRef = useRef(chartFilters);
  chartFiltersRef.current = chartFilters;

  const executeFetch = useCallback(
    (
      schedulesOverride?: SelectedSchedulesState,
      {
        requireSelectedIds = false,
        chartFiltersOverride,
      }: {
        requireSelectedIds?: boolean;
        chartFiltersOverride?: CheckpointRoutesChartFilters;
      } = {},
    ) => {
      const {
        orgId: currentOrgId,
        selectedDivs: currentSelectedDivs,
        queryDivisionIds: currentQueryDivisionIds,
      } = fetchParamsRef.current;
      const chart = chartFiltersOverride ?? {
        relative: fetchParamsRef.current.relative,
        dateRange: fetchParamsRef.current.dateRange,
        groupBy: fetchParamsRef.current.groupBy,
        schedules: schedulesOverride ?? fetchParamsRef.current.selectedSchedules,
      };
      const schedules = schedulesOverride ?? chart.schedules;
      const { relative: nextRelative, dateRange: nextDateRange, groupBy: nextGroupBy } = chart;
      const {
        dateType: nextDateType,
        isRelative: nextIsRelative,
        relativeAmount: nextRelativeAmount,
      } = nextRelative;

      if (requireSelectedIds && Object.keys(schedules.byId).length === 0) {
        return;
      }

      if (!currentOrgId || !currentSelectedDivs.length) {
        return;
      }

      const range = getRange(nextDateRange, nextDateType, nextIsRelative, nextRelativeAmount, SCHEDULES_REPORT_MAX_SPAN, fetchParamsRef.current.organizationTimeZone, true);
      setLoading(true);
      apiCallable(API_OPERATIONS.SCHEDULE_DASHBOARD)({
        organizationId: currentOrgId,
        divisionIds: currentQueryDivisionIds,
        uid: auth.currentUser?.uid,
        range,
        calendarRange: getCalendarRange(range, fetchParamsRef.current.organizationTimeZone),
        interval: nextGroupBy,
        scheduleIds: schedules.selectedAll ? [] : Object.keys(schedules.byId),
        type: SCHEDULE_TYPES.checkpoint_route,
      })
        .then(result => {
          const routesById = getDashboardSchedulesById(result);
          const { data } = camelize(result) as {
            data: {
              calendarTimeZone?: string;
              aggregations: { timeRange: { histogram: { buckets: TimeAggregationBucket[] } } };
            };
          };
          aggregationTimeZoneRef.current = data.calendarTimeZone || null;
          setAggregationTimeZone(aggregationTimeZoneRef.current || 'UTC');
          const buckets = data.aggregations.timeRange.histogram.buckets;
          setAggregations(buckets);
          setRoutesById(routesById);
          setLoading(false);
        })
        .catch(error => {
          console.error('🔍 [CheckpointRoutesDashboard] API Error:', error);
          setAggregations([]);
          setRoutesById({});
          setLoading(false);
        });
    },
    [],
  );

  const historyRef = useRef(history);
  historyRef.current = history;

  const urlRef = useRef(url);
  urlRef.current = url;

  const toggleDrawer = useCallback((type: ScheduleDashboardDrawerType) => {
    setDrawerState(prev => ({
      ...prev,
      [type]: !prev[type],
    }));
  }, []);

  const runQuery = useCallback(() => {
    executeFetch();
  }, [executeFetch]);

  const applyFilter = useCallback(
    (props: SetValueProps | SetValueProps[]) => {
      const propsList = restoreClearedReportFilters(
        props,
        CHECKPOINT_ROUTES_DASHBOARD_FILTER_FIELDS,
        () => buildInitCheckpointRoutesFilterValues(buildDefaultCheckpointRoutesChartFilters(), intlRef.current),
      );
      if (!propsList.length) return;

      const baseFilters = Object.keys(filtersRef.current).length
        ? filtersRef.current
        : initialFiltersRef.current;
      const nextFilters = propsList.reduce(mergeFilterSetValueProps, baseFilters);

      selectedSchedulesRef.current = filterValuesToCheckpointRoutesFilters(nextFilters).schedules;
      filtersRef.current = nextFilters;
      propsList.forEach(setFilters);
    },
    [setFilters],
  );

  const applyFilters = useCallback(() => {
    const baseFilters = Object.keys(filtersRef.current).length
      ? filtersRef.current
      : initialFiltersRef.current;
    const nextChartFilters = {
      ...filterValuesToCheckpointRoutesFilters(baseFilters),
      schedules: selectedSchedulesRef.current,
    };
    setChartFilters(nextChartFilters);
    executeFetch(undefined, {
      chartFiltersOverride: nextChartFilters,
      requireSelectedIds: !nextChartFilters.schedules.selectedAll,
    });
  }, [executeFetch]);

  const setSelectedSchedules = useCallback(
    (next: SelectedSchedulesState | ((prev: SelectedSchedulesState) => SelectedSchedulesState)) => {
      const currentSelection = selectedSchedulesRef.current;
      const resolved = typeof next === 'function' ? next(currentSelection) : next;

      if (areScheduleSelectionsEqual(currentSelection, resolved)) {
        return;
      }
      selectedSchedulesRef.current = resolved;

      const scheduleProps = buildCheckpointRoutesFilterSetValueProps(
        CHECKPOINT_ROUTES_DASHBOARD_FILTER_FIELDS.SCHEDULES,
        resolved,
        intlRef.current,
      );
      setFilters(scheduleProps);
      filtersRef.current = mergeFilterSetValueProps(
        filtersRef.current,
        scheduleProps,
      );
    },
    [setFilters],
  );

  const goToTimeline = useCallback(
    ({
      divisionId,
      scheduleId,
      triggerID,
      dateKeyArg,
      groupByArg,
      finishedExecutions,
      expectedExecutions,
    }: GoToTimelineParams) => {
      const {
        orgId: currentOrgId,
        dateRange: currentDateRange,
        dateType: currentDateType,
        isRelative: currentIsRelative,
        relativeAmount: currentRelativeAmount,
      } = fetchParamsRef.current;

      const range = getRange(
        currentDateRange,
        currentDateType,
        currentIsRelative,
        currentRelativeAmount,
        SCHEDULES_REPORT_MAX_SPAN,
        fetchParamsRef.current.organizationTimeZone,
        true,
      );
      let path = getFullUrl(
        getRouteToCheckpointRoutesDrawer(
          currentOrgId,
          divisionId,
          triggerID,
          scheduleId,
          range.gte,
          range.lte,
          dateKeyArg,
          groupByArg,
        ),
        urlRef.current,
      );

      if (finishedExecutions != null && expectedExecutions != null) {
        const separator = path.includes('?') ? '&' : '?';
        path = `${path}${separator}finishedExecutions=${finishedExecutions}&expectedExecutions=${expectedExecutions}`;
      }

      if (aggregationTimeZoneRef.current) path += `&calendarTimeZone=${encodeURIComponent(aggregationTimeZoneRef.current)}`;
      historyRef.current.push(path, { shadow: true });
    },
    [],
  );

  const onBackFromTimeline = useCallback(() => {
    if (shadow) history.goBack();
    else history.push(getFullUrl(PATHS.CHECKPOINT_ROUTES, url));
  }, [shadow, history, url]);

  const mainLoading = loading || scheduleSelectorLoading;

  const divisionNameSourceRoutesById = useMemo(
    () => ({ ...selectedSchedules.byId, ...routesById }),
    [selectedSchedules.byId, routesById],
  );
  const dashboardDivisionIds = useMemo(
    () => getDashboardScheduleDivisionIds(divisionNameSourceRoutesById),
    [divisionNameSourceRoutesById],
  );
  const { getDivisionName } = useDivisionNames({
    organizationId: orgId,
    divisionIds: dashboardDivisionIds,
    assignedDivisions,
  });
  const routesByIdWithDivisionNames = useMemo(
    () =>
      getDashboardSchedulesWithDivisionNames(
        divisionNameSourceRoutesById,
        getDivisionName,
      ),
    [divisionNameSourceRoutesById, getDivisionName],
  );

  const tableDataByDay = useMemo<TableDataByDay[]>(() => {
    return aggregations
      .slice()
      .reverse()
      .map(day => {
        const hasSchedule = !!day.bySchedule.buckets.length;
        if (!hasSchedule) return null;

        const dataSource = day.bySchedule.buckets
          .map(schAggregation => {
            const sch = routesByIdWithDivisionNames[schAggregation.key];
            if (!sch) return null;

            const scheduleData = calculateScheduleProgress(schAggregation);

            return {
              id: `${day.key}-${schAggregation.key}`,
              dateKey: day.key,
              schedule: sch,
              aggregation: schAggregation,
              scheduleData,
            };
          })
          .filter((row): row is NonNullable<typeof row> => row !== null);

        return {
          day: Number.isFinite(Number(day.key)) ? DateUtil.toDateString(Number(day.key), 'YYYY-MM-DD', aggregationTimeZone) : day.key,
          dataSource,
        };
      })
      .filter((dayData): dayData is TableDataByDay => dayData !== null);
  }, [aggregationTimeZone, aggregations, routesByIdWithDivisionNames]);

  useEffect(() => {
    set(
      CHECKPOINT_ROUTES_DASHBOARD_FILTERS_STORAGE_KEY,
      checkpointRoutesFiltersToStoragePayload(draftChartFilters),
    );
  }, [draftChartFilters]);

  const selectedDivsKey = [...selectedDivs].sort().join(',');
  const queryDivisionIdsKey = queryDivisionIds.join(',');

  useEffect(() => {
    if (!orgId || !selectedDivsKey) {
      return;
    }

    // `queryDivisionIds` can collapse to `[]` (org-wide "all divisions") after the
    // selector ids are already set. Key on both so that collapse refetches.
    const fetchKey = `${orgId}:${selectedDivsKey}:${queryDivisionIdsKey}:${organizationTimeZone}`;
    if (initialFetchKeyRef.current === fetchKey) {
      return;
    }

    initialFetchKeyRef.current = fetchKey;
    // The panel is shared across screens and only seeds `filters` with ours in a mount effect, so
    // during this commit `filters` is still empty or holds the previous screen's values. Querying
    // the restored filters explicitly keeps the buckets aligned with the groupBy the panel is
    // about to show — otherwise a foreign `groupBy` labels, say, daily buckets as whole months.
    executeFetch(undefined, { chartFiltersOverride: chartFiltersRef.current });
  }, [executeFetch, initialChartFilters, orgId, organizationTimeZone, queryDivisionIdsKey, selectedDivsKey]);

  const value = useMemo<CheckpointRoutesDashboardContextValue>(
    () => ({
      aggregations,
      chartFilters,
      draftChartFilters,
      hasPendingFilters,
      filters,
      initialFilters: initialFiltersRef.current,
      isRelative,
      relativeAmount,
      dateType,
      dateRange,
      selectedSchedules,
      loading,
      scheduleSelectorLoading,
      mainLoading,
      groupBy,
      tableDataByDay,
      selectedUsers,
      drawerState,
      queryDivisionIds,
      orgId,
      divId,
      triggerId,
      dateKey,
      openTimelineEvents,
      disabledDate: disableCalendarDatesAfterToday,
      intl,
      url,
      setFilters: applyFilter,
      applyFilters,
      setSelectedSchedules,
      setScheduleSelectorLoading,
      setSelectedUsers,
      fetch: runQuery,
      goToTimeline,
      onBackFromTimeline,
      toggleDrawer,
      toggle,
      isOpen,
    }),
    [
      aggregations,
      chartFilters,
      draftChartFilters,
      hasPendingFilters,
      filters,
      isRelative,
      relativeAmount,
      dateType,
      dateRange,
      selectedSchedules,
      loading,
      scheduleSelectorLoading,
      mainLoading,
      groupBy,
      tableDataByDay,
      selectedUsers,
      drawerState,
      queryDivisionIds,
      orgId,
      divId,
      triggerId,
      dateKey,
      openTimelineEvents,
      intl,
      url,
      applyFilter,
      applyFilters,
      setSelectedSchedules,
      runQuery,
      goToTimeline,
      onBackFromTimeline,
      toggleDrawer,
      toggle,
      isOpen,
    ],
  );

  return (
    <CheckpointRoutesDashboardContext.Provider value={value}>
      {children}
    </CheckpointRoutesDashboardContext.Provider>
  );
};

export const useCheckpointRoutesDashboardContext = (): CheckpointRoutesDashboardContextValue => {
  const context = useContext(CheckpointRoutesDashboardContext);
  if (!context) {
    throw new Error(
      'useCheckpointRoutesDashboardContext must be used within a CheckpointRoutesDashboardProvider',
    );
  }
  return context;
};
